import { create } from 'zustand'
import { jwtDecode } from 'jwt-decode'
import { apiClient } from '../services/api'

interface TokenPayload {
    user_id: string
    email: string
    role: string
    exp: number
    iat: number
}

interface User {
    id: string
    email: string
    name?: string
    role: string
}

interface AuthStore {
    token: string | null
    user: User | null
    loading: boolean
    error: string | null
    initialized: boolean

    login: (email: string, password: string) => Promise<void>
    logout: () => void
    initializeAuth: () => void
    isTokenValid: () => boolean
    getAuthHeader: () => Record<string, string>
}

export const useAuthStore = create<AuthStore>((set, get) => ({
    token: null,
    user: null,
    loading: false,
    error: null,
    initialized: false,

    login: async (email: string, password: string) => {
        try {
            set({ loading: true, error: null })

            const response = await apiClient.login(email, password)

            if (!response || !response.data) {
                throw new Error('Login failed - no response data')
            }

            const data = response.data
            if (!data.access_token) {
                throw new Error('Login failed - no access token')
            }

            const decoded = jwtDecode<TokenPayload>(data.access_token)

            console.log('[Auth] Decoded JWT:', decoded)

            const user: User = {
                id: decoded.user_id,
                email: decoded.email,
                role: decoded.role,
                name: data.user?.name
            }

            console.log('[Auth] User object to save:', user)

            localStorage.setItem('auth_token', data.access_token)
            localStorage.setItem('user', JSON.stringify(user))

            set({ token: data.access_token, user, loading: false })
        } catch (error) {
            console.error('[Auth] Login error:', error)
            const axiosErr = error as { response?: { data?: { error?: string } } }
            const message =
                axiosErr.response?.data?.error ||
                (error instanceof Error ? error.message : 'Login failed')
            set({ error: message, loading: false })
            throw error
        }
    },

    logout: async () => {
        try {
            console.log('[Auth] logout called - calling backend API')

            // Call backend logout endpoint to record logout event
            await apiClient.logout()
        } catch (error) {
            console.error('[Auth] Error during logout API call:', error)
        } finally {
            // Always clear local state
            console.log('[Auth] Clearing token and user from localStorage')
            localStorage.removeItem('auth_token')
            localStorage.removeItem('user')
            set({ token: null, user: null })
        }
    },

    initializeAuth: () => {
        console.log('[Auth] initializeAuth starting')
        const token = localStorage.getItem('auth_token')
        const userStr = localStorage.getItem('user')

        console.log('[Auth] localStorage check - token exists:', !!token, 'user exists:', !!userStr)

        if (token && userStr) {
            try {
                const payload = jwtDecode<TokenPayload>(token)
                const now = Math.floor(Date.now() / 1000)

                console.log('[Auth] Token validation - exp:', payload.exp, 'now:', now, 'valid:', payload.exp > now)

                if (payload.exp > now) {
                    const user = JSON.parse(userStr)
                    console.log('[Auth] Token valid, setting auth state with user:', user.email)
                    set({ token, user, initialized: true })
                } else {
                    // Token expired - record timeout event
                    console.log('[Auth] Token expired, recording timeout')
                    const user = JSON.parse(userStr)

                    // Call backend to record the timeout
                    fetch('/api/auth/session-timeout', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            userId: user.id,
                            email: user.email
                        })
                    }).catch((err) => {
                        console.error('[Auth] Failed to record timeout:', err)
                    })

                    localStorage.removeItem('auth_token')
                    localStorage.removeItem('user')
                    set({ token: null, user: null, initialized: true })
                }
            } catch (e) {
                console.error('[Auth] Error in initializeAuth:', e)
                localStorage.removeItem('auth_token')
                localStorage.removeItem('user')
                set({ token: null, user: null, initialized: true })
            }
        } else {
            console.log('[Auth] No token/user in localStorage, clearing auth state')
            set({ token: null, user: null, initialized: true })
        }
    },

    isTokenValid: () => {
        const state = get()
        if (!state.token) return false

        try {
            const payload = jwtDecode<TokenPayload>(state.token)
            const now = Math.floor(Date.now() / 1000)
            return payload.exp > now
        } catch {
            return false
        }
    },

    getAuthHeader: (): Record<string, string> => {
        const state = get()
        return state.token ? { Authorization: `Bearer ${state.token}` } : {}
    }
}))
